import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useId } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { testIdAttr } from '@/lib/testIds';

/** Read by a screen reader, not shown. */
const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

interface ActionToggleProps {
  /** What the switch turns on: "Donate", "Volunteer". Also the switch's name. */
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** One line under the label: what the button opens. */
  hint?: string;
  /** Where the thing the switch shows is managed, as words and an address. */
  link?: { label: string; to: string };
  disabled?: boolean;
  /** A save is on its way: the switch shows the new state and waits. */
  pending?: boolean;
  /**
   * `<testId>-switch` lands on the `<input>`, so Playwright's `check()` and
   * `uncheck()` work on it; `<testId>-hint` and `<testId>-link` on the rest.
   */
  testId: string;
}

/**
 * A switch with its label, a hint line and a link to where the thing it turns
 * on is managed - the ARC's Donate and Volunteer buttons, which the
 * Fundraising and Volunteer Recruitment pages fill in.
 *
 * The label names the switch, and the hint is read with it, so a screen
 * reader hears "Donate, switch, off, External fundraising link".
 */
export function ActionToggle({
  label,
  checked,
  onChange,
  hint,
  link,
  disabled = false,
  pending = false,
  testId,
}: ActionToggleProps) {
  const labelId = useId();
  const hintId = useId();

  return (
    <Box
      data-testid={testId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Switch
          checked={checked}
          disabled={disabled || pending}
          onChange={(event) => onChange(event.target.checked)}
          slotProps={{
            input: {
              'aria-labelledby': labelId,
              'aria-describedby': hint ? hintId : undefined,
              ...testIdAttr(`${testId}-switch`),
            },
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography id={labelId} variant="body1" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          {hint ? (
            <Typography
              id={hintId}
              variant="body2"
              color="text.secondary"
              data-testid={`${testId}-hint`}
            >
              {hint}
            </Typography>
          ) : null}
        </Box>
        {pending ? (
          // The spinner is decoration; the words say it, so a screen reader
          // hears "Saving Donate" rather than an unnamed progress bar.
          <>
            <CircularProgress size={16} aria-hidden />
            <Box component="span" sx={VISUALLY_HIDDEN}>
              Saving {label}
            </Box>
          </>
        ) : null}
      </Box>
      {link ? (
        <Link
          component={RouterLink}
          to={link.to}
          variant="body2"
          data-testid={`${testId}-link`}
        >
          {link.label}
        </Link>
      ) : null}
    </Box>
  );
}
